import {
  createRouter as createVueRouter,
  createWebHistory,
  type Router,
} from 'vue-router';

import { getCurrentUser } from '@/api/apiClient/auth';
import { useAuthStore } from '@/features/auth/authStore';
import { useConfigStore } from '@/features/config/configStore';
import { getAnalytics } from '@/providers/analytics';
import { getFirebase } from '@/providers/firebase';

/**
 * Creates and configures the Vue Router with authentication guards
 *
 * @returns Configured Router instance with routes and guards
 */
export const createRouter = (): Router => {
  const router = createVueRouter({
    history: createWebHistory(),
    routes: [
      {
        path: '/',
        name: 'home',
        component: () => import('@/pages/HomePage.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: '/search',
        name: 'search',
        component: () => import('@/pages/SearchResultsPage.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: '/login',
        name: 'login',
        component: () => import('@/pages/LoginPage.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: '/skillsets/:name',
        name: 'skillset-detail',
        component: () => import('@/pages/SkillsetDetailPage.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: '/skills/:name',
        name: 'skill-detail',
        component: () => import('@/pages/SkillDetailPage.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: '/subagents/:name',
        name: 'subagent-detail',
        component: () => import('@/pages/SubagentDetailPage.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: '/for-teams',
        name: 'internal-registry',
        component: () => import('@/pages/InternalRegistryPage.vue'),
        meta: { requiresAuth: false, publicInstanceOnly: true },
      },
      {
        path: '/publish',
        name: 'publish',
        component: () => import('@/pages/PublishPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: '/users/:email',
        name: 'user-profile',
        component: () => import('@/pages/UserProfilePage.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: '/pending',
        name: 'my-pending',
        component: () => import('@/pages/MyPendingPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: '/admin/users',
        name: 'admin-users',
        component: () => import('@/pages/AdminUsersPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: '/admin/users/:uid',
        name: 'admin-users-detail',
        component: () => import('@/pages/UserDetailPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: '/admin/settings',
        name: 'admin-settings',
        component: () => import('@/pages/AdminSettingsPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: '/builder',
        name: 'skillset-builder',
        component: () => import('@/pages/SkillsetBuilderPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: '/docs/:pathMatch(.*)*',
        name: 'docs',
        component: () => import('@/pages/DocsPage.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: '/access-denied',
        name: 'access-denied',
        component: () => import('@/pages/AccessDeniedPage.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: '/oauth/callback',
        name: 'oauth-callback',
        component: () => import('@/pages/OAuthCallbackPage.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: '/transcripts',
        name: 'transcripts',
        component: () => import('@/pages/TranscriptsPage.vue'),
        meta: { requiresAuth: true, privateInstanceOnly: true },
      },
      {
        path: '/team-transcripts',
        name: 'team-transcripts',
        component: () => import('@/pages/TeamTranscriptsPage.vue'),
        meta: { requiresAuth: true, privateInstanceOnly: true },
      },
      {
        path: '/skill-generation',
        name: 'skill-generation',
        component: () => import('@/pages/SkillGenerationPage.vue'),
        meta: {
          requiresAuth: true,
          privateInstanceOnly: true,
          requiresSkillGen: true,
        },
      },
    ],
  });

  // Auth guard
  router.beforeEach(async (to, from, next) => {
    // Ensure Firebase finishes restoring auth state before checking currentUser.
    // In tests, Firebase may not be configured, so skip the wait if unavailable.
    const firebase = getFirebase();
    try {
      await firebase.waitForAuthReady();
    } catch (error) {
      console.warn(
        'Firebase not configured; skipping auth restore wait.',
        error,
      );
    }

    const user = getCurrentUser();
    const authStore = useAuthStore();
    const configStore = useConfigStore();
    const requiresAuth = to.meta.requiresAuth;

    // Routes that don't require login even in private instance mode
    const publicRoutes = ['login', 'access-denied', 'oauth-callback'];
    const isPublicPage = publicRoutes.includes(to.name as string);

    // Routes marked publicInstanceOnly should redirect to home on private instances
    if (to.meta.publicInstanceOnly && configStore.isPrivateInstance) {
      next('/');
      return;
    }

    // Routes marked privateInstanceOnly should redirect to home on public instances
    if (to.meta.privateInstanceOnly && !configStore.isPrivateInstance) {
      next('/');
      return;
    }

    // Routes requiring skill generation should redirect to home when feature is disabled
    if (to.meta.requiresSkillGen && !configStore.skillGenEnabled) {
      next('/');
      return;
    }

    // Skip auth redirects while Firebase is still restoring auth state
    if (authStore.isInitializing) {
      next();
      return;
    }

    // In private instance mode, require auth for all pages except public pages
    if (configStore.isPrivateInstance && !user && !isPublicPage) {
      next('/login');
      return;
    }

    if (requiresAuth && !user) {
      // Redirect to login if route requires auth and user is not logged in
      next('/login');
    } else {
      // Track analytics on path change
      if (to.path !== from.path) {
        const analytics = getAnalytics();
        const trackingParams: {
          path: string;
          title: string;
          userId?: string;
        } = {
          path: to.path,
          title: (to.name as string) || to.path,
        };

        if (user?.email) {
          trackingParams.userId = user.email;
        }

        analytics.trackPageView(trackingParams);
      }
      next();
    }
  });

  return router;
};
